import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useCallback, useMemo, useRef } from 'react';
import { ChatRole, QueryKeys } from '../constants';
import { usePusherChannel } from '../contexts/PusherContext';
import processChunkedPusherItem from './numinaChunkReassembly';
import { useFetch } from './utils';

export function useSession(surveyId, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.SESSION, surveyId],
    queryFn: () =>
      fetcher({
        path: `v1/numina/session/?survey_id=${surveyId}`,
      }),
    enabled: !!surveyId, // Only run query if surveyId is provided
    staleTime: Infinity, // Never consider data stale
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on mount if data exists
    refetchOnReconnect: false, // Don't refetch on reconnect
    ...options,
  });
}

/**
 * Hook for sending messages with real-time streaming via Pusher
 *
 * This hook:
 * 1. Sends the message to the backend (returns immediately with task_id and channel)
 * 2. Subscribes to Pusher channel for real-time updates
 * 3. Updates the query cache incrementally as events arrive
 */
export function useSendMessage(surveyId, options = {}) {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  const channelRef = useRef(null);

  return useMutation({
    mutationFn: ({ data }) =>
      fetcher({
        path: 'v1/numina/session/message/',
        method: 'PUT',
        data: JSON.stringify({ ...data, survey_id: surveyId }),
      }),
    onMutate: async ({ data }) => {
      await queryClient.cancelQueries([QueryKeys.SESSION, surveyId]);
      const prevItems = queryClient.getQueryData([QueryKeys.SESSION, surveyId]);

      // Add user message item and loading placeholder immediately
      queryClient.setQueryData([QueryKeys.SESSION, surveyId], (prev) => {
        const userItem = {
          type: 'text',
          role: ChatRole.USER,
          text: data.message,
          timestamp: new Date().toISOString(),
        };

        // Add loading placeholder
        const loadingItem = {
          loading: true,
        };

        return {
          ...prev,
          data: [...(prev?.data || []), userItem, loadingItem],
        };
      });

      return { prevItems };
    },
    onSuccess: (response) => {
      // Response contains: { task_id, channel_name, status }
      const { channel_name: channelName } = response.data;

      if (!channelName) {
        return;
      }

      // Store reference to current channel
      channelRef.current = channelName;
    },
    onError: (_error, _data, context) => {
      queryClient.setQueryData([QueryKeys.SESSION, surveyId], context.prevItems);
      message.error('Failed to send message');
    },
    ...options,
  });
}

/**
 * Hook to subscribe to Pusher channel and handle streaming message updates
 *
 * This should be used in conjunction with useSendMessage to receive real-time updates
 */
export function usePusherSubscription(surveyId, channelName) {
  const queryClient = useQueryClient();
  // Buffer for reassembling chunked messages
  const chunkBufferRef = useRef({});

  // Define event handlers
  const handleMessageUpdate = useCallback(
    (data) => {
      const { type, data: itemData } = data;

      // Check if this is a chunked message
      const isChunked = itemData.chunk_index !== undefined && itemData.chunk_total !== undefined;

      let processedItemData = itemData;

      if (isChunked) {
        const result = processChunkedPusherItem({
          chunkBuffer: chunkBufferRef.current,
          type,
          itemData,
          logger: console,
        });

        // Update the chunk buffer
        chunkBufferRef.current = result.chunkBuffer;

        if (result.status === 'pending') {
          // Still waiting for more chunks, don't process yet
          return;
        }

        if (result.status === 'drop') {
          // Invalid chunk or error, skip processing
          return;
        }

        processedItemData = result.itemData;
      }

      const updater = (prev) => {
        if (!prev || !prev.data) {
          return prev;
        }

        const items = [...prev.data];
        const loadingIndex = items.findIndex((item) => item.loading);

        if (loadingIndex === -1) {
          return prev;
        }

        // Remove the loading placeholder
        items.splice(loadingIndex, 1);

        // Handle different event types
        if (type === 'text') {
          // Add text item with role and mark as new (from Pusher)
          items.push({ ...processedItemData, isNewMessage: true });
        } else if (type === 'tool') {
          // Add tool call (without response yet)
          items.push(processedItemData);
        } else if (type === 'tool-response') {
          // Find matching tool call and update its response
          const toolIndex = [...items]
            .reverse()
            .findIndex(
              (item) =>
                item.type === 'tool' &&
                item.name === processedItemData.name &&
                item.response === null,
            );

          if (toolIndex !== -1) {
            const actualIndex = items.length - 1 - toolIndex;
            items[actualIndex] = { ...items[actualIndex], response: processedItemData.response };
          } else {
            // No matching tool call found, add as new item
            items.push(processedItemData);
          }
        }

        // Add back loading placeholder at the end
        items.push({ loading: true });

        return {
          response: { ...prev.response },
          data: items,
        };
      };

      queryClient.setQueryData([QueryKeys.SESSION, surveyId], updater);
    },
    [queryClient, surveyId],
  );

  const handleMessageComplete = useCallback(() => {
    // Remove loading placeholder and ensure all tools are marked complete
    queryClient.setQueryData([QueryKeys.SESSION, surveyId], (prev) => {
      if (!prev) return prev;

      const items = prev.data
        .filter((item) => !item.loading)
        .map((item) => {
          // Ensure all tool items have a response (mark pending tools as complete)
          if (item.type === 'tool' && (item.response === null || item.response === undefined)) {
            return { ...item, response: {} };
          }
          return item;
        });

      return {
        response: { ...prev.response },
        data: items,
      };
    });
  }, [queryClient, surveyId]);

  const handleMessageError = useCallback(
    (data) => {
      message.error(`Error: ${data.error}`);

      // Remove loading placeholder and show error
      queryClient.setQueryData([QueryKeys.SESSION, surveyId], (prev) => {
        if (!prev) return prev;

        const items = prev.data.filter((item) => !item.loading);
        return { ...prev, data: items };
      });
    },
    [queryClient, surveyId],
  );

  const handleSessionEnded = useCallback(() => {
    // Optionally show a message to the user
  }, []);

  // Subscribe to Pusher channel with event handlers
  const events = useMemo(
    () => ({
      'message-update': handleMessageUpdate,
      'message-complete': handleMessageComplete,
      'message-error': handleMessageError,
      'session-ended': handleSessionEnded,
    }),
    [handleMessageUpdate, handleMessageComplete, handleMessageError, handleSessionEnded],
  );

  usePusherChannel(channelName, events);
}

export function useEndSession(surveyId) {
  const fetcher = useFetch();
  return useMutation({
    mutationFn: ({ data }) =>
      fetcher({
        path: 'v1/numina/session/end/',
        method: 'DELETE',
        data: JSON.stringify({ ...data, survey_id: surveyId }),
      }),
    onError: () => {
      message.error('Failed to end session');
    },
  });
}

export function useSessionSurveyIds(surveyId, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.SESSION_SURVEY_IDS, surveyId],
    queryFn: () =>
      fetcher({
        path: `v1/numina/session/surveys/?survey_id=${surveyId}`,
      }),
    enabled: !!surveyId, // Only run query if surveyId is provided
    ...options,
  });
}

export function useUpdateSurveyIds(surveyId) {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }) =>
      fetcher({
        path: 'v1/numina/session/surveys/update/',
        method: 'PUT',
        data: JSON.stringify({ ...data, survey_id: surveyId }),
      }),
    onSettled: () => queryClient.invalidateQueries([QueryKeys.SESSION_SURVEY_IDS, surveyId]),
    onError: () => {
      message.error('Failed to update survey context');
    },
  });
}

/**
 * Hook to fetch cached dataset by result_key
 */
export function useCachedDataset(resultKey, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.CACHED_DATASET, resultKey],
    queryFn: () =>
      fetcher({
        path: `v1/numina/assets/dataset/${resultKey}/`,
      }),
    enabled: !!resultKey,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Hook to fetch chart spec by chart_name
 */
export function useChartSpec(chartName, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.CHART_SPEC, chartName],
    queryFn: () =>
      fetcher({
        path: `v1/numina/assets/chart/${chartName}/`,
      }),
    enabled: !!chartName,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Hook to submit thumbs-up/down feedback on an agent response
 */
export function useSessionFeedback(surveyId, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.SESSION_FEEDBACK, surveyId],
    queryFn: () =>
      fetcher({
        path: `v1/numina/session/feedback/?survey_id=${surveyId}`,
      }),
    enabled: !!surveyId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useSubmitFeedback(surveyId) {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ messageIndex, rating, comment }) =>
      fetcher({
        path: 'v1/numina/session/feedback/',
        method: 'POST',
        data: JSON.stringify({
          survey_id: surveyId,
          message_index: messageIndex,
          rating,
          comment,
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.SESSION_FEEDBACK, surveyId]);
    },
    onError: () => {
      message.error('Failed to submit feedback');
    },
  });
}
